import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Building2, Calendar, Users, MapPin } from 'lucide-react';
import { departureCities, nightOptions, accommodationOptions, roomTypes } from '../data/mockData';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Input } from './ui/input';
import { Button } from './ui/button';

const InquiryForm = ({ onOpenBuilder }) => {
  const [formData, setFormData] = useState({
    departureCity: '',
    departureDate: '',
    returnDate: '',
    phone: '',
    email: '',
    makkahNights: '',
    madinaNights: '',
    adults: '1',
    children: '',
    infants: '',
    accommodation: '',
    roomType: ''
  });

  const [currentSlide, setCurrentSlide] = useState(0);
  const slides = [
    {
      title: "Build Your Umrah",
      subtitle: "Customize your spiritual journey"
    },
    {
      title: "Ramadan Special",
      subtitle: "Experience Ramadan in the Holy Land"
    },
    {
      title: "Family Packages",
      subtitle: "Affordable Umrah for the whole family"
    }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Open Zamzam Umrah Builder
    onOpenBuilder();
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <section id="builder" className="relative py-8 bg-gray-100">
      {/* Slider Header */}
      <div className="relative h-20 bg-gradient-to-r from-gray-200 to-gray-300 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 h-full flex items-center justify-between">
          <button
            onClick={prevSlide}
            className="w-10 h-10 bg-[#d4af37] rounded-full flex items-center justify-center hover:bg-[#c4a030] transition-colors"
          >
            <ChevronLeft className="w-6 h-6 text-white" />
          </button>
          
          <div className="text-center">
            <h2 
              className="text-2xl md:text-3xl font-bold text-gray-800"
              style={{ fontFamily: 'Playfair Display, Georgia, serif', fontStyle: 'italic' }}
            >
              {slides[currentSlide].title}
            </h2>
            <p className="text-gray-600 text-sm mt-1">{slides[currentSlide].subtitle}</p>
          </div>
          
          <button
            onClick={nextSlide}
            className="w-10 h-10 bg-[#d4af37] rounded-full flex items-center justify-center hover:bg-[#c4a030] transition-colors"
          >
            <ChevronRight className="w-6 h-6 text-white" />
          </button>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-6xl mx-auto px-4 -mt-2">
        <div className="bg-[#d4af37] rounded-lg p-6 shadow-xl">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="w-6 h-6 text-black" />
            <h3 className="text-black font-bold text-lg">BUILD YOUR UMRAH PACKAGE</h3>
          </div>

          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Departure City */}
            <div>
              <label className="block text-black text-sm font-medium mb-1">
                <MapPin className="w-4 h-4 inline mr-1" />
                Departure City
              </label>
              <Select onValueChange={(value) => handleInputChange('departureCity', value)}>
                <SelectTrigger className="bg-white border-0">
                  <SelectValue placeholder="Select City" />
                </SelectTrigger>
                <SelectContent>
                  {departureCities.map((city) => (
                    <SelectItem key={city} value={city}>
                      {city}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Departure Date */}
            <div>
              <label className="block text-black text-sm font-medium mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                Travel Date
              </label>
              <Input
                type="date"
                className="bg-white border-0"
                onChange={(e) => handleInputChange('departureDate', e.target.value)}
              />
            </div>

            {/* Makkah Nights */}
            <div>
              <label className="block text-black text-sm font-medium mb-1">Makkah Nights</label>
              <Select onValueChange={(value) => handleInputChange('makkahNights', value)}>
                <SelectTrigger className="bg-white border-0">
                  <SelectValue placeholder="Select Nights" />
                </SelectTrigger>
                <SelectContent>
                  {nightOptions.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Madinah Nights */}
            <div>
              <label className="block text-black text-sm font-medium mb-1">Madinah Nights</label>
              <Select onValueChange={(value) => handleInputChange('madinaNights', value)}>
                <SelectTrigger className="bg-white border-0">
                  <SelectValue placeholder="Select Nights" />
                </SelectTrigger>
                <SelectContent>
                  {nightOptions.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Accommodation */}
            <div>
              <label className="block text-black text-sm font-medium mb-1">Hotel Category</label>
              <Select onValueChange={(value) => handleInputChange('accommodation', value)}>
                <SelectTrigger className="bg-white border-0">
                  <SelectValue placeholder="Select Category" />
                </SelectTrigger>
                <SelectContent>
                  {accommodationOptions.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Room Type */}
            <div>
              <label className="block text-black text-sm font-medium mb-1">Room Type</label>
              <Select onValueChange={(value) => handleInputChange('roomType', value)}>
                <SelectTrigger className="bg-white border-0">
                  <SelectValue placeholder="Select Room" />
                </SelectTrigger>
                <SelectContent>
                  {roomTypes.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Adults */}
            <div>
              <label className="block text-black text-sm font-medium mb-1">
                <Users className="w-4 h-4 inline mr-1" />
                Adults
              </label>
              <Select 
                defaultValue="1"
                onValueChange={(value) => handleInputChange('adults', value)}
              >
                <SelectTrigger className="bg-white border-0">
                  <SelectValue placeholder="1" />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                    <SelectItem key={num} value={num.toString()}>
                      {num} Adult{num > 1 ? 's' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Children */}
            <div>
              <label className="block text-black text-sm font-medium mb-1">Children (2-11)</label>
              <Select onValueChange={(value) => handleInputChange('children', value)}>
                <SelectTrigger className="bg-white border-0">
                  <SelectValue placeholder="0 Children" />
                </SelectTrigger>
                <SelectContent>
                  {[0, 1, 2, 3, 4, 5].map((num) => (
                    <SelectItem key={num} value={num.toString()}>
                      {num} {num === 1 ? 'Child' : 'Children'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Phone */}
            <div>
              <label className="block text-black text-sm font-medium mb-1">Phone Number</label>
              <Input
                type="tel"
                placeholder="+44 XXX XXX XXXX"
                className="bg-white border-0"
                onChange={(e) => handleInputChange('phone', e.target.value)}
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-black text-sm font-medium mb-1">Email Address</label>
              <Input
                type="email"
                placeholder="your@email.com"
                className="bg-white border-0"
                onChange={(e) => handleInputChange('email', e.target.value)}
              />
            </div>

            {/* Submit Button */}
            <div className="md:col-span-2 flex items-end">
              <Button
                type="submit"
                className="bg-black text-white px-8 py-3 hover:bg-gray-800 transition-colors font-bold w-full"
              >
                BUILD MY UMRAH PACKAGE
              </Button>
            </div>
          </form>

          {/* No Flights Notice */}
          <div className="mt-4 text-center">
            <p className="text-black/70 text-sm">
              <strong>Note:</strong> This is an Umrah-only service. Hotels, transport, visa, and ground services included.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default InquiryForm;
